import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Coins, Info } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '../ui/command';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import ConfirmDialog from '../ConfirmDialog';
import { updateOrganisation } from '../../services/organisation.service';
import { useAuth } from '../../context/authContext';
import { useToast } from '../ui/use-toast';
import {
    CURRENCIES,
    POPULAR_CURRENCY_CODES,
    formatCurrencyLabel,
    getCurrencyByCode,
} from '../../data/currencies';
import { cn } from '../../utils';

const DEFAULT_TOLERANCE_TYPE = 'PERCENTAGE';

const TOLERANCE_TOOLTIP =
    'When a converted total only just crosses into a higher workflow tier, within this tolerance of the boundary, the lower tier is selected instead. Set to 0 to disable.';

const CurrencyOption = ({ currency, selected, onSelect }) => (
    <CommandItem
        key={currency.code}
        value={`${currency.code} ${currency.name}`}
        onSelect={() => onSelect(currency.code)}
    >
        <Check className={cn('mr-2 h-4 w-4', selected === currency.code ? 'opacity-100' : 'opacity-0')} />
        <span className="font-medium">{currency.code}</span>
        <span className="ml-2 text-slate-500">{currency.name}</span>
    </CommandItem>
);

export default function BaseCurrencyCard({
    baseCurrency,
    convertToBaseCurrency,
    tierToleranceType,
    tierToleranceValue,
    onUpdated,
}) {
    const { user } = useAuth();
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(baseCurrency || '');
    const [convert, setConvert] = useState(!!convertToBaseCurrency);
    const [toleranceType, setToleranceType] = useState(tierToleranceType || DEFAULT_TOLERANCE_TYPE);
    const [toleranceValue, setToleranceValue] = useState(tierToleranceValue ?? 0);

    useEffect(() => {
        setSelected(baseCurrency || '');
        setConvert(!!convertToBaseCurrency);
        setToleranceType(tierToleranceType || DEFAULT_TOLERANCE_TYPE);
        setToleranceValue(tierToleranceValue ?? 0);
    }, [baseCurrency, convertToBaseCurrency, tierToleranceType, tierToleranceValue]);

    const savedCurrency = baseCurrency || '';
    const savedConvert = !!convertToBaseCurrency;
    const savedToleranceType = tierToleranceType || DEFAULT_TOLERANCE_TYPE;
    const savedToleranceValue = Number(tierToleranceValue ?? 0);
    const numericTolerance = Number(toleranceValue) || 0;
    const isDirty =
        selected !== savedCurrency ||
        convert !== savedConvert ||
        toleranceType !== savedToleranceType ||
        numericTolerance !== savedToleranceValue;
    const canSave = Boolean(selected) && isDirty;

    const popularCurrencies = useMemo(
        () => POPULAR_CURRENCY_CODES.map((code) => getCurrencyByCode(code)).filter(Boolean),
        []
    );

    const otherCurrencies = useMemo(
        () => CURRENCIES.filter((currency) => !POPULAR_CURRENCY_CODES.includes(currency.code)),
        []
    );

    const isPercentage = toleranceType === 'PERCENTAGE';
    const toleranceUnit = isPercentage ? '%' : (selected || baseCurrency || '');

    const handleSelect = (code) => {
        setSelected(code);
        setOpen(false);
    };

    const handleConfirm = async () => {
        const response = await updateOrganisation(
            user.organisationId,
            {
                baseCurrency: selected,
                convertToBaseCurrency: convert,
                tierToleranceType: toleranceType,
                tierToleranceValue: numericTolerance,
            },
            user.token
        );

        if (response && response.status === 200) {
            toast({
                title: 'Success',
                description: 'Base currency settings updated',
                variant: 'success',
            });
            onUpdated && onUpdated();
        } else {
            const message = response?.data?.error?.message || 'Error updating base currency settings';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
                        <Coins className="h-4 w-4 text-slate-500" />
                    </span>
                    <h3 className="my-0">Base Currency</h3>
                </div>
            </CardHeader>
            <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                    Set the default currency for workflow value tiers and organisation reporting.
                </p>

                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="w-full justify-between font-normal"
                        >
                            {selected
                                ? formatCurrencyLabel(selected)
                                : 'Select base currency...'}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command>
                            <CommandInput placeholder="Search currencies..." />
                            <CommandList>
                                <CommandEmpty>No currencies found.</CommandEmpty>
                                <CommandGroup heading="Popular">
                                    {popularCurrencies.map((currency) => (
                                        <CurrencyOption
                                            key={currency.code}
                                            currency={currency}
                                            selected={selected}
                                            onSelect={handleSelect}
                                        />
                                    ))}
                                </CommandGroup>
                                <CommandSeparator />
                                <CommandGroup heading="All currencies">
                                    {otherCurrencies.map((currency) => (
                                        <CurrencyOption
                                            key={currency.code}
                                            currency={currency}
                                            selected={selected}
                                            onSelect={handleSelect}
                                        />
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>

                <label className="flex items-start gap-3 mt-6 cursor-pointer">
                    <Switch
                        checked={convert}
                        onCheckedChange={setConvert}
                        className="mt-0.5"
                    />
                    <span className="text-sm text-gray-600 leading-snug">
                        Convert to base currency
                    </span>
                </label>

                {convert && (
                    <div className="mt-4 flex items-center gap-2">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="flex items-center gap-1.5 text-sm text-gray-600 shrink-0 cursor-default">
                                        Tolerance
                                        <Info className="h-3.5 w-3.5 text-slate-400" />
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                    <p className="text-xs mb-0">{TOLERANCE_TOOLTIP}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <Select value={toleranceType} onValueChange={setToleranceType}>
                            <SelectTrigger className="w-[100px] h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="PERCENTAGE">Percent</SelectItem>
                                <SelectItem value="FIXED">Fixed</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="relative flex-1 min-w-0">
                            <Input
                                type="number"
                                min="0"
                                step={isPercentage ? '0.1' : '1'}
                                value={toleranceValue}
                                onChange={(e) =>
                                    setToleranceValue(e.target.value === '' ? 0 : Number(e.target.value))
                                }
                                className="h-9 pr-10 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                                {toleranceUnit}
                            </span>
                        </div>
                    </div>
                )}

                {canSave ? (
                    <ConfirmDialog
                        title="Update Base Currency"
                        description="Save changes to the organisation base currency settings?"
                        cancel="Cancel"
                        action="Update"
                        handleAction={handleConfirm}
                    >
                        <Button className="mt-6 yvr-button">
                            Save Changes
                        </Button>
                    </ConfirmDialog>
                ) : (
                    <Button disabled className="mt-6 yvr-button">
                        Save Changes
                    </Button>
                )}
            </CardContent>
        </Card>
    );
}
